import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Wallet, ArrowUpRight, ArrowDownLeft,
  Bot, Code, Gift, Tag, Settings, LogOut,
  Shield, Menu, X, ChevronRight,
} from 'lucide-react';
import useAuthStore from '../store/authStore';

const P  = '#C29500';
const P2 = '#E8C040';

const KudiLogo = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="sl-g" x1="0" y1="0" x2="80" y2="80" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor={P2}/>
        <stop offset="100%" stopColor={P}/>
      </linearGradient>
    </defs>
    <rect width="80" height="80" rx="20" fill="url(#sl-g)"/>
    <rect x="20" y="18" width="9" height="44" rx="4.5" fill="#fff"/>
    <path d="M29 40 L54 20" stroke="#fff" strokeWidth="9" strokeLinecap="round"/>
    <path d="M29 40 L54 60" stroke="#fff" strokeWidth="9" strokeLinecap="round"/>
    <path d="M42 56 L54 60 L50 48" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const navItems = [
  { icon: <LayoutDashboard size={17}/>, label: 'Dashboard',   path: '/dashboard'   },
  { icon: <Wallet size={17}/>,          label: 'Wallet',       path: '/wallet'       },
  { icon: <ArrowUpRight size={17}/>,    label: 'Payouts',      path: '/withdrawals'  },
  { icon: <ArrowDownLeft size={17}/>,   label: 'Collections',  path: '/collections'  },
  { icon: <Bot size={17}/>,             label: 'Bot',          path: '/bot'          },
  { icon: <Code size={17}/>,            label: 'Developer',    path: '/developer'    },
  { icon: <Gift size={17}/>,            label: 'Referral',     path: '/referral'     },
  { icon: <Tag size={17}/>,             label: 'Pricing',      path: '/pricing'      },
  { icon: <Settings size={17}/>,        label: 'Settings',     path: '/settings'     },
];

const PAGE_TITLES = {
  '/dashboard':   'Dashboard',
  '/wallet':      'Wallet',
  '/withdrawals': 'Payouts',
  '/collections': 'Collections',
  '/bot':         'Bot',
  '/developer':   'Developer',
  '/referral':    'Referral',
  '/pricing':     'Pricing',
  '/settings':    'Settings',
  '/admin':       'Admin',
};

function SidebarContent({ onClose }) {
  const { logout, user } = useAuthStore();
  const location = useLocation();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Link to="/" onClick={onClose}
          style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <KudiLogo size={30} />
          <span style={{ fontSize: 17, fontWeight: 900, color: '#fff', letterSpacing: -0.5 }}>
            Kudi<span style={{ color: P2 }}>bot</span>
          </span>
        </Link>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <p style={{ fontSize: 10, fontWeight: 700, color: '#334155', letterSpacing: 1.5,
          textTransform: 'uppercase', padding: '6px 10px 4px', marginBottom: 2 }}>Main Menu</p>

        {navItems.map((item) => {
          const active = location.pathname === item.path;
          return (
            <Link key={item.path} to={item.path} onClick={onClose}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderRadius: 8, textDecoration: 'none',
                fontSize: 13, fontWeight: active ? 600 : 500,
                color: active ? P2 : '#64748B',
                background: active ? `${P}12` : 'transparent',
                borderLeft: active ? `2px solid ${P}` : '2px solid transparent',
                transition: 'all .15s',
              }}
              onMouseOver={e => { if (!active) { e.currentTarget.style.color = '#CBD5E1'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}}
              onMouseOut={e => { if (!active) { e.currentTarget.style.color = '#64748B'; e.currentTarget.style.background = 'transparent'; }}}>
              <span style={{ color: active ? P : '#475569' }}>{item.icon}</span>
              {item.label}
            </Link>
          );
        })}

        {/* Admin (admins only) */}
        {user?.is_admin && (
          <>
            <p style={{ fontSize: 10, fontWeight: 700, color: '#334155', letterSpacing: 1.5,
              textTransform: 'uppercase', padding: '14px 10px 4px' }}>Admin</p>
            <Link to="/admin" onClick={onClose}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderRadius: 8, textDecoration: 'none',
                fontSize: 13, fontWeight: location.pathname === '/admin' ? 600 : 500,
                color: location.pathname === '/admin' ? '#F87171' : '#64748B',
                background: location.pathname === '/admin' ? 'rgba(239,68,68,0.1)' : 'transparent',
                borderLeft: location.pathname === '/admin' ? '2px solid #EF4444' : '2px solid transparent',
              }}>
              <Shield size={17} style={{ color: location.pathname === '/admin' ? '#EF4444' : '#475569' }}/>
              Admin Panel
            </Link>
          </>
        )}
      </nav>

      {/* User + Logout */}
      <div style={{ padding: '12px 10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {user && (
          <div style={{
            padding: '10px 12px', borderRadius: 8, marginBottom: 4,
            background: 'rgba(255,255,255,0.03)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                background: `linear-gradient(135deg, ${P}, #06B6D4)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 800, color: '#fff',
              }}>
                {(user.full_name || user.email || '?')[0].toUpperCase()}
              </div>
              <div style={{ overflow: 'hidden', flex: 1 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#CBD5E1', margin: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.full_name || 'User'}
                </p>
                <p style={{ fontSize: 11, color: '#475569', margin: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.email}
                </p>
              </div>
            </div>
          </div>
        )}
        <button onClick={logout}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            padding: '9px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: 'transparent', color: '#64748B', fontSize: 13, fontWeight: 500,
            transition: 'all .15s',
          }}
          onMouseOver={e => { e.currentTarget.style.color = '#F87171'; e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
          onMouseOut={e => { e.currentTarget.style.color = '#64748B'; e.currentTarget.style.background = 'transparent'; }}>
          <LogOut size={16}/> Sign Out
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location        = useLocation();
  const title           = PAGE_TITLES[location.pathname] || 'Kudibot';

  return (
    <div style={{ minHeight: '100vh', background: '#09090B', display: 'flex', fontFamily: 'Inter, sans-serif' }}>

      {/* Desktop Sidebar */}
      <aside style={{
        display: 'none', width: 220, background: '#0D0D12',
        borderRight: '1px solid rgba(255,255,255,0.06)',
        position: 'fixed', height: '100vh', top: 0, left: 0, zIndex: 40,
        flexDirection: 'column',
      }} className="sidebar-desktop">
        <SidebarContent onClose={() => {}} />
      </aside>

      {/* Mobile top bar */}
      <div style={{
        display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        background: 'rgba(13,13,18,0.95)', backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        padding: '0 16px', height: 52,
        alignItems: 'center', justifyContent: 'space-between',
      }} className="mobile-topbar">
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <KudiLogo size={26} />
          <span style={{ fontSize: 15, fontWeight: 900, color: '#fff' }}>
            Kudi<span style={{ color: P2 }}>bot</span>
          </span>
        </Link>
        <button onClick={() => setOpen(!open)}
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 8, padding: '6px 8px', cursor: 'pointer', color: '#94A3B8', display: 'flex' }}>
          {open ? <X size={18}/> : <Menu size={18}/>}
        </button>
      </div>

      {/* Mobile Sidebar Drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 40 }}
              className="mobile-overlay"/>
            <motion.aside
              initial={{ x: -240 }} animate={{ x: 0 }} exit={{ x: -240 }}
              transition={{ type: 'tween', duration: 0.22 }}
              style={{
                position: 'fixed', left: 0, top: 0, height: '100%', width: 240,
                background: '#0D0D12', borderRight: '1px solid rgba(255,255,255,0.06)',
                zIndex: 50,
              }}>
              <SidebarContent onClose={() => setOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main */}
      <main style={{ flex: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
        className="main-content">

        {/* Desktop page header */}
        <div style={{
          padding: '0 32px', height: 56, display: 'flex', alignItems: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          background: 'rgba(13,13,18,0.8)', backdropFilter: 'blur(20px)',
          position: 'sticky', top: 0, zIndex: 30,
        }} className="desktop-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569' }}>
            <span>Kudibot</span>
            <ChevronRight size={13}/>
            <span style={{ color: '#CBD5E1', fontWeight: 600 }}>{title}</span>
          </div>
        </div>

        <div style={{ flex: 1, padding: '0' }}>
          {children}
        </div>
      </main>

      {/* WhatsApp */}
      <a href="https://wa.me/447860088648?text=Hi%20Kudibot%20Support%2C%20I%20need%20help"
        target="_blank" rel="noopener noreferrer"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}>
        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}
          style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'linear-gradient(135deg,#25D366,#128C7E)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(37,211,102,0.35)', position: 'relative',
          }}>
          <span style={{ position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'rgba(37,211,102,0.3)', animation: 'waping 1.5s infinite' }}/>
          <svg viewBox="0 0 32 32" style={{ width: 22, height: 22, fill: 'white', position: 'relative', zIndex: 1 }}>
            <path d="M16.004 2.667C8.637 2.667 2.667 8.637 2.667 16c0 2.347.64 4.64 1.853 6.64L2.667 29.333l6.907-1.813A13.267 13.267 0 0 0 16.004 29.333c7.363 0 13.329-5.97 13.329-13.333S23.367 2.667 16.004 2.667zm0 24c-2.107 0-4.16-.573-5.947-1.653l-.427-.253-4.093 1.08 1.093-4-.28-.44A10.613 10.613 0 0 1 5.333 16c0-5.88 4.787-10.667 10.667-10.667S26.667 10.12 26.667 16 21.88 26.667 16 26.667zm5.84-7.987c-.32-.16-1.893-.933-2.187-1.04-.293-.107-.507-.16-.72.16-.213.32-.827 1.04-.987 1.253-.16.213-.347.24-.667.08-.32-.16-1.347-.493-2.56-1.573-.947-.84-1.587-1.88-1.773-2.2-.187-.32-.02-.493.14-.653.147-.147.32-.373.48-.56.16-.187.213-.32.32-.533.107-.213.053-.4-.027-.56-.08-.16-.72-1.733-.987-2.373-.253-.613-.52-.533-.72-.547h-.613c-.213 0-.56.08-.853.4-.293.32-1.12 1.093-1.12 2.667s1.147 3.093 1.307 3.307c.16.213 2.253 3.44 5.467 4.827.763.333 1.36.533 1.827.68.773.24 1.467.213 2.013.133.613-.093 1.893-.773 2.16-1.52.267-.747.267-1.387.187-1.52-.08-.133-.293-.213-.613-.373z"/>
          </svg>
        </motion.div>
      </a>

      <style>{`
        @media (min-width: 1024px) {
          .sidebar-desktop { display: flex !important; }
          .mobile-topbar   { display: none !important; }
          .mobile-overlay  { display: none !important; }
          .main-content    { margin-left: 220px; padding-top: 0; }
          .desktop-header  { display: flex !important; }
        }
        @media (max-width: 1023px) {
          .main-content    { padding-top: 52px; }
          .desktop-header  { display: none !important; }
        }
        @keyframes waping { 0%,100%{transform:scale(1);opacity:.7} 50%{transform:scale(1.4);opacity:0} }
      `}</style>
    </div>
  );
}
